<template>
	<!-- eslint-disable max-lines-->
	<div class="filtering-interface">
		<div
			v-if="availableFilterFields.length === 0"
			class="no-fields-message"
		>
			<v-icon
				color="grey"
				class="mr-2"
			>
				mdi-filter-outline
			</v-icon>
			<span class="text-body-2 text--secondary">
				No filterable fields available. Please select fields first.
			</span>
		</div>

		<v-data-table
			v-else
			:items="availableFilterFields"
			:headers="filterTableHeaders"
			item-key="fieldName"
			dense
			hide-default-footer
			disable-pagination
			disable-sort
			class="filter-table"
		>
			<template #item="{ item }">
				<tr>
					<td
						class="text-left"
						:class="{ 'align-content-start': hasMultiValues(item) }"
					>
						<div
							class="field-info text-center"
							:class="{ 'field-has-multi-values mt-1': hasMultiValues(item) }"
						>
							<v-icon
								small
								class="field-icon"
								color="primary"
							>
								{{ getFieldIcon(item.typeFriendlyName) }}
							</v-icon>
							<span class="field-name">
								{{ item.displayName || item.fieldName }}
							</span>
						</div>
					</td>
					<td
						class="text-center"
						:class="{ 'align-content-start mt-1': hasMultiValues(item) }"
					>
						<div :class="{ 'operator-top-margin': hasMultiValues(item) }">
							<template v-if="isFieldFilterable(item)">
								<FilterOperatorCell
									:field="item"
									:filter-data="getFilterForField(item.fieldName)"
									:editing-cell="editingCell"
									:user-full-permissions="userFullPermissions"
									:lookup-data="lookupData"
									@focus-cell="handleFocusCell"
									@filter-change="handleFilterChangeEvent"
									@stop-editing="stopEditing"
									@cancel-editing="cancelEditing"
								/>
							</template>
							<template v-else>
								<span class="text-body-2 text--secondary not-filterable-msg">
									Unfilterable
								</span>
							</template>
						</div>
					</td>
					<td class="text-center">
						<template v-if="isFieldFilterable(item)">
							<FilterValueCell
								:field="item"
								:filter-data="getFilterForField(item.fieldName)"
								:editing-cell="editingCell"
								:user-full-permissions="userFullPermissions"
								@focus-cell="handleFocusCell"
								@filter-change="handleFilterChangeEvent"
							/>
						</template>
					</td>
					<td
						class="text-center"
						:class="{ 'align-content-start': hasMultiValues(item) }"
					>
						<div
							class="actions-cell text-center"
							:class="{ 'actions-has-multi-values': hasMultiValues(item) }"
						>
							<v-menu
								v-if="isFieldFilterable(item) && hasFilter(item.fieldName)"
								bottom
								left
								offset-y
							>
								<template #activator="{ on, attrs }">
									<v-btn
										icon
										small
										v-bind="attrs"
										v-on="on"
										@click.stop
									>
										<v-icon small>
											mdi-dots-horizontal
										</v-icon>
									</v-btn>
								</template>
								<v-list dense>
									<v-list-item @click="resetFilter(item.fieldName)">
										<v-list-item-content>
											<v-list-item-title>
												Reset Filter
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
								</v-list>
							</v-menu>
						</div>
					</td>
				</tr>
			</template>
		</v-data-table>
	</div>
</template>

<script>
/* eslint-disable max-lines */
import { VIcon, VDataTable, VBtn, VMenu, VList, VListItem, VListItemContent, VListItemTitle } from 'vuetify/lib/components'
import { getFieldIcon } from '../integration-builder/filterUtils.js'
import FilterOperatorCell from './FilterOperatorCell.vue'
import FilterValueCell from './FilterValueCell.vue'
// No longer need filter utility imports since we're working with nested format directly

export default {
	name: 'filtering-interface',
	components: {
		VIcon,
		VDataTable,
		VBtn,
		VMenu,
		VList,
		VListItem,
		VListItemContent,
		VListItemTitle,
		FilterOperatorCell,
		FilterValueCell
	},
	props: {
		cassieObjectId: {
			type: Number,
			default: null
		},
		dataTypeItems: {
			type: Array,
			default: () => []
		},
		availableFields: {
			type: Array,
			default: () => []
		},
		userFullPermissions: {
			type: Boolean,
			default: false
		},
		initialFilters: {
			type: Array,
			default: () => []
		},
		lookupData: {
			type: Object,
			default: () => ({})
		}
	},
	data () {
		return {
			activeFilters: [],
			editingCell: null,
			originalValue: null
		}
	},
	computed: {
		availableFilterFields () {
			// Use the actual available fields from the parent component
			// Filter to only include fields that are marked as filterable
			return this.availableFields
		},
		filterTableHeaders () {
			return [
				{ text: 'Field', value: 'field', width: '25%', sortable: false, align: 'left' },
				{ text: 'Operator', value: 'operator', width: '25%', sortable: false, align: 'center' },
				{ text: 'Value', value: 'value', width: '30%', sortable: false, align: 'center' },
				{ text: 'Actions', value: 'actions', width: '10%', sortable: false, align: 'center', cellClass: item => this.hasMultiValues(item) ? 'align-content-start' : '' }
			]
		}
	},
	watch: {
		initialFilters: {
			immediate: true,
			deep: true,
			handler (newFilters) {
				// Store filters directly in nested format (API format)
				if (JSON.stringify(this.activeFilters) !== JSON.stringify(newFilters)) {
					this.activeFilters = [...newFilters]
				}
			}
		},
		availableFields: {
			immediate: true,
			handler (newFields) {
				// Fields changed - component will reactively update
			}
		}
	},
	created () {
		// Component initialization
	},
	methods: {
		hasMultiValues (fieldItem) {
			const f = this.getFilterForField(fieldItem.fieldName) || {}
			const rawOp = (f.operator ?? '').toString()
			const op = rawOp.toLowerCase().replace(/[\s_-]+/g, '')
			const multiOps = new Set(['includes', 'doesnotinclude', 'in', 'notin'])
			const isTargetOp = multiOps.has(op)
			const val = f.value
			let hasValues = false
			if (Array.isArray(val)) {
				hasValues = val.length > 0
			} else if (val !== null && val !== undefined) {
				const s = String(val).trim()
				hasValues = s.length > 0
			}
			return isTargetOp && hasValues
		},
		isFieldFilterable (field) {
			if (!field || typeof field !== 'object') return true
			// If any explicit false flags exist, consider it not filterable
			const negativeFlags = ['isFilterable', 'filterable', 'supportsFiltering', 'allowFiltering']
			for (const key of negativeFlags) {
				if (field[key] === false) return false
			}
			// If any explicit true flags exist, it's filterable
			for (const key of negativeFlags) {
				if (field[key] === true) return true
			}
			// Default to filterable when unspecified to preserve current behavior
			return true
		},
		getFieldByName (fieldName) {
			return this.availableFilterFields.find(field => field.fieldName === fieldName)
		},
		getFilterForField (fieldName) {
			// Find filter in nested format
			const filter = this.activeFilters.find(filter => filter.fieldName === fieldName)
			if (filter) {
				// Convert nested format to flat format for UI components
				const isBetweenOperator = filter.filter.operator === 'BETWEEN' || filter.filter.operator === 'NOT BETWEEN'
				let value = null

				if (filter.filter.values && filter.filter.values.length > 0) {
					if (isBetweenOperator) {
						// For BETWEEN operators, create array with both values
						value = [
							filter.filter.values[0]?.value || null,
							filter.filter.values[1]?.value || null
						]
					} else if (['includes', 'does not include', 'in', 'not in'].includes(String(filter.filter.operator || '').toLowerCase())) {
						// For multi-value operators, keep as array of values
						value = filter.filter.values.map(v => v.value)
					} else {
						// For single value operators, use first value
						value = filter.filter.values[0].value
					}
				}

				return {
					fieldName: filter.fieldName,
					operator: filter.filter.operator,
					value: value,
					dataType: this.getFieldByName(fieldName)?.dataType || 'string'
				}
			}

			// Return empty filter for new fields
			return {
				fieldName,
				operator: null,
				value: null,
				dataType: this.getFieldByName(fieldName)?.dataType || 'string'
			}
		},
		getFieldIcon (typeFriendlyName) {
			return getFieldIcon(typeFriendlyName)
		},
		convertFlatToNested (filterData) {
			// Convert flat filter data to nested API format
			let values
			if (Array.isArray(filterData.value)) {
				// Handle array values (e.g., BETWEEN or INCLUDES operators)
				values = filterData.value.map(val => ({ value: val }))
			} else {
				// Handle single values
				values = [{ value: filterData.value }]
			}
			return {
				fieldName: filterData.fieldName,
				filter: {
					operator: filterData.operator,
					values: values
				}
			}
		},
		updateFilter (filterData) {
			// Convert flat filter data to nested format
			const nestedFilter = this.convertFlatToNested(filterData)

			// Update or add filter in nested format
			const existingIndex = this.activeFilters.findIndex(filter => filter.fieldName === filterData.fieldName)

			if (existingIndex >= 0) {
				// Update existing filter
				this.$set(this.activeFilters, existingIndex, nestedFilter)
			} else {
				// Add new filter
				this.activeFilters.push(nestedFilter)
			}

			// Emit directly without transformation since we're storing in API format
			this.$emit('update:filters', this.activeFilters)
		},
		handleFocusCell (focusData) {
			this.focusCell(null, focusData.fieldName, focusData.field)
		},
		handleFilterChangeEvent (changeData) {
			this.handleFilterChange(changeData.fieldName, changeData.field, changeData.value)
		},

		// Inline editing methods
		focusCell (event, fieldName, field) {
			if (!this.userFullPermissions) return
			this.editingCell = { fieldName, field }
			const currentFilter = this.getFilterForField(fieldName)
			this.originalValue = currentFilter[field] || ''

			this.$nextTick(() => {
				const editInput = this.$refs.editInput
				if (editInput) {
					// Handle both single element and array of elements
					const element = Array.isArray(editInput) ? editInput[0] : editInput
					if (element) {
						element.focus()

						// For select elements, the @focus="$event.target.click()" in template handles opening

						// For text inputs, select the text
						if (field === 'value' && element.select) {
							element.select()
						}
					}
				}
			})
		},

		stopEditing () {
			this.editingCell = null
			this.originalValue = null
		},

		cancelEditing () {
			if (this.editingCell && this.originalValue !== null) {
				this.handleFilterChange(this.editingCell.fieldName, this.editingCell.field, this.originalValue)
			}
			this.stopEditing()
		},

		handleFilterChange (fieldName, field, value) {
			const currentFilter = this.getFilterForField(fieldName)
			const updatedFilter = {
				...currentFilter,
				fieldName,
				dataType: this.getFieldByName(fieldName)?.dataType || 'string',
				[field]: value
			}

			// Handle operator changes
			if (field === 'operator') {
				const lower = String(value || '').toLowerCase()
				const isBetweenOperator = value === 'BETWEEN' || value === 'NOT BETWEEN'
				const isMultiOperator = ['includes', 'does not include', 'in', 'not in'].includes(lower)
				if (isBetweenOperator && !Array.isArray(currentFilter.value)) {
					// BETWEEN: convert single value to array
					updatedFilter.value = [currentFilter.value || null, null]
				} else if (isMultiOperator && !Array.isArray(currentFilter.value)) {
					// Multi: initialize array
					updatedFilter.value = currentFilter.value != null && currentFilter.value !== '' ? [currentFilter.value] : []
				} else if (!isBetweenOperator && !isMultiOperator && Array.isArray(currentFilter.value)) {
					// Other: back to single
					updatedFilter.value = currentFilter.value[0] || null
				}
			}

			this.updateFilter(updatedFilter)
		},

		hasFilter (fieldName) {
			// Check if filter exists in nested format and has an operator
			const filter = this.activeFilters.find(filter => filter.fieldName === fieldName)
			return !!(filter && filter.filter && filter.filter.operator)
		},
		resetFilter (fieldName) {
			// Remove the filter from activeFilters (nested format)
			this.activeFilters = this.activeFilters.filter(filter => filter.fieldName !== fieldName)

			// Emit the updated filters to parent (already in API format)
			this.$emit('update:filters', this.activeFilters)

			// Stop any current editing
			if (this.editingCell && this.editingCell.fieldName === fieldName) {
				this.stopEditing()
			}
		}
	}
}
</script>

<style lang="scss" scoped>
@import '../shared/stepper-styles.scss';

.filtering-interface {
	display: flex;
	flex-direction: column;
	gap: 12px;
	max-width: 1000px;
	margin-bottom: 16px;
}

.no-fields-message {
	@extend .empty-state;
}

.not-filterable-msg { font-size: 0.8rem; }

.filter-table {
	border: 1px solid var(--global-border-color);
	border-radius: var(--radius, 8px);
	margin-top: 12px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
	overflow: hidden;
	width: 100%;
	min-width: 900px;

	:deep(.v-data-table__wrapper) {
		border-radius: var(--radius, 8px);
	}

	:deep(.v-data-table tbody tr:hover) {
		background-color: rgba(33, 150, 243, 0.03) !important;
	}

	:deep(.v-data-table tbody tr:not(:last-child)) {
		border-bottom: 1px solid var(--global-main-background-color);
	}

	:deep(.v-data-table__empty-wrapper) {
		display: none;
	}

	:deep(.v-data-table-header) {
		background-color: var(--global-main-background-color);
	}

	:deep(.v-data-table-header th) {
		text-align: center !important;
		font-weight: 600;
		color: var(--global-foreground);
		padding: 12px 16px;
		border-bottom: 2px solid var(--global-border-color);
	}
}

.type-cell-content { display: flex; justify-content: center; align-items: center; }

.operator-cell,
.value-cell {
	padding: 0 !important;
	vertical-align: middle;
	position: relative;
	transition: background-color 0.2s ease;

	&.editable-cell {
		cursor: pointer;

		&:hover:not(.editing) {
			background-color: rgba(33, 150, 243, 0.05);
		}

		&.empty-cell .cell-display {
			color: var(--global-muted-foreground);
			font-style: italic;
		}
	}
}

.actions-cell { display: flex; justify-content: center; align-items: center; padding: 8px; }

.field-name {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 0;
}

.field-icon {
	flex-shrink: 0;
	background-color: rgba(33, 150, 243, 0.1);
	padding: 4px;
	border-radius: var(--radius-sm, 4px);
}

.field-name {
	font-size: 14px;
	font-weight: 500;
	color: var(--global-foreground);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.cell-display {
	display: block;
	padding: 12px 16px;
	min-height: 20px;
	line-height: 20px;
	font-size: 14px;
	width: 100%;
	box-sizing: border-box;
	border-radius: var(--radius-sm, 4px);
	transition: all 0.2s ease;
}

.cell-input,
.cell-select {
	width: 100%;
	border: none;
	background: rgba(33, 150, 243, 0.03);
	padding: 12px 16px;
	font-size: 14px;
	line-height: 20px;
	outline: none;
	box-sizing: border-box;
	border-radius: var(--radius-sm, 4px);
	transition: all 0.2s ease;

	&:focus {
		background-color: white;
		box-shadow: inset 0 0 0 2px var(--global-color-blue-darken-2);
	}

	&:hover:not(:focus) {
		background-color: rgba(33, 150, 243, 0.05);
	}
}

@media (max-width: 768px) {
	.filter-table {
		font-size: 13px;

		thead th {
			padding: 12px;
			font-size: 12px;
		}

		tbody td {
			padding: 12px;
		}
	}

	.field-column {
		width: 35%;
	}

	.type-column {
		width: 20%;
	}

	.filter-column {
		width: 45%;
	}
}

.operator-top-margin {
	margin-top: 5px;
}

::v-deep .v-input{margin-bottom:0}::v-deep .v-text-field{margin-top:0}::v-deep .v-select{margin-top:0}:deep(.v-data-table tbody tr){transition:background-color .2s ease}:deep(.v-data-table td){padding:8px 12px;vertical-align:top!important}
</style>
